export interface CompetitorData {
  slug: string;
  name: string;
  fullName: string;
  website: string;
  location: string;
  phone: string;
  yearsInMarket: number;
  eventsCount: string;
  equipmentCount: string;
  satisfactionRate: number;
  geoCoverage: string;
  geoCoverageScore: number;
  specializationScore: number;
  equipmentScore: number;
  experienceScore: number;
  serviceScore: number;
  priceScore: number;
  techScore: number;
  supportScore: number;
  overallScore: number;
  tagline: string;
  description: string;
  keyDifferentiator: string;
  strengths: string[];
  weaknesses: string[];
  idealFor: string[];
  comparisonCategories: {
    label: string;
    stibValue: string;
    competitorValue: string;
    winner: 'stib' | 'competitor' | 'tie';
  }[];
  faqs: { question: string; answer: string }[];
  metaTitle: string;
  metaDescription: string;
  heroImageSeq: string;
}

// ─── STIB base data for comparison ───────────────────────────────────────────

export const STIB_BASE = {
  name: 'STIB',
  fullName: 'STIB - Palestra Silenciosa São Paulo',
  website: 'https://stibpalestrasilenciosasp.com.br',
  location: 'São Paulo, SP',
  phone: '(11) 91777-6227',
  yearsInMarket: 8,
  eventsCount: '500+',
  equipmentCount: '5.000+',
  satisfactionRate: 98,
  geoCoverage: '9 cidades em SP + 11 capitais brasileiras',
  tagline: 'Especialistas em fones sem fio para eventos',
  keyDifferentiator: 'Empresa 100% focada em aluguel de fones sem fio para eventos, com cobertura estratégica em 9 cidades de São Paulo e capacidade para 5.000+ participantes simultâneos.',
};

// ─── Competitor 1: World Sound Translation (WST) ───────────────────────────

export const WST_DATA: CompetitorData = {
  slug: 'stib-vs-world-sound-translation',
  name: 'World Sound Translation',
  fullName: 'World Sound Translation (WST)',
  website: 'worldsoundtranslation.com',
  location: 'São Paulo, SP',
  phone: '(11) 94481-1166',
  yearsInMarket: 10,
  eventsCount: 'Não divulgado',
  equipmentCount: 'Não divulgado',
  satisfactionRate: 0,
  geoCoverage: 'São Paulo (foco)',
  geoCoverageScore: 5,
  specializationScore: 4,
  equipmentScore: 5,
  experienceScore: 6,
  serviceScore: 6,
  priceScore: 4,
  techScore: 5,
  supportScore: 6,
  overallScore: 5.1,
  tagline: 'Agência full de produção de eventos e tradução',
  description:
    'A World Sound Translation é uma agência de produção de eventos que oferece serviços amplos: produção de eventos, tradução simultânea, palestra silenciosa, equipamento de tradução, transmissão ao vivo e muito mais. Com clientes como Sesc, Vale, Senac, USP, Nações Unidas e Faber-Castell, a WST é uma empresa consolidada no mercado de eventos corporativos em São Paulo.',
  keyDifferentiator: 'Suporte 24 horas e portfólio diversificado que inclui produção completa de eventos, não apenas locação de equipamentos.',
  strengths: [
    'Suporte 24 horas para eventos',
    'Portfólio de grandes clientes corporativos',
    'Serviços integrados (produção + áudio + tradução)',
    'Blog ativo com conteúdo sobre o setor',
  ],
  weaknesses: [
    'Palestra silenciosa é um serviço secundário, não o core business',
    'Não divulga quantidade de equipamentos disponíveis',
    'Cobertura geográfica limitada principalmente a São Paulo',
    'Foco mais amplo significa menos especialização em fones',
    'Não informa satisfação média dos clientes',
  ],
  idealFor: [
    'Empresas que precisam de produção completa de eventos',
    'Eventos que exigem múltiplos serviços simultâneos (tradução + áudio + vídeo)',
    'Clientes que valorizam suporte 24h',
  ],
  comparisonCategories: [
    {
      label: 'Especialização em Fones',
      stibValue: '100% focada — fones são o core business',
      competitorValue: 'Serviço secundário dentro de portfólio amplo',
      winner: 'stib',
    },
    {
      label: 'Cobertura Geográfica',
      stibValue: '9 cidades em SP + 11 capitais brasileiras',
      competitorValue: 'Foco em São Paulo e região',
      winner: 'stib',
    },
    {
      label: 'Equipamentos Disponíveis',
      stibValue: '5.000+ fones sem fio',
      competitorValue: 'Quantidade não divulgada',
      winner: 'stib',
    },
    {
      label: 'Experiência no Mercado',
      stibValue: '8 anos — 500+ eventos',
      competitorValue: '10+ anos — portfólio diversificado',
      winner: 'competitor',
    },
    {
      label: 'Suporte Técnico',
      stibValue: 'Presencial em 9 cidades de SP',
      competitorValue: 'Suporte 24h (remoto + presencial)',
      winner: 'competitor',
    },
    {
      label: 'Preço Competitivo',
      stibValue: 'Especialização reduz custos operacionais',
      competitorValue: 'Preço premium por serviços integrados',
      winner: 'stib',
    },
    {
      label: 'Tecnologia dos Fones',
      stibValue: 'Fones sem fio RF de última geração',
      competitorValue: 'Tecnologia padrão de mercado',
      winner: 'stib',
    },
    {
      label: 'Portfólio de Clientes',
      stibValue: '780+ clientes corporativos',
      competitorValue: 'Sesc, Vale, Senac, USP, ONU',
      winner: 'competitor',
    },
  ],
  faqs: [
    {
      question: 'A STIB é mais barata que a World Sound Translation?',
      answer:
        'Geralmente sim. Como a STIB é 100% especializada em aluguel de fones sem fio, nossos custos operacionais são mais enxutos. A World Sound Translation oferece um pacote premium com produção completa de eventos, o que naturalmente eleva o preço. Para quem precisa apenas de fones, a STIB oferece melhor custo-benefício.',
    },
    {
      question: 'A World Sound Translation oferece palestra silenciosa como serviço principal?',
      answer:
        'Não. A palestra silenciosa é um dos serviços oferecidos pela WST, mas o core business da empresa é produção completa de eventos e tradução simultânea. Isso significa que a expertise em fones pode não ser tão aprofundada quanto a de uma empresa 100% dedicada.',
    },
    {
      question: 'Qual empresa tem mais experiência no mercado?',
      answer:
        'A World Sound Translation opera há mais de 10 anos, enquanto a STIB tem 8 anos. Porém, a STIB realizou 500+ eventos específicos de palestra silenciosa, enquanto a WST não divulga o volume de eventos nesse nicho específico. Em termos de volume focado, a STIB tem mais experiência prática.',
    },
    {
      question: 'A WST atende fora de São Paulo?',
      answer:
        'A WST tem foco em São Paulo. A STIB, por outro lado, tem cobertura em 9 cidades do estado de São Paulo (Campinas, Barueri, SJC, Ribeirão Preto, Santos, Guarulhos, SBC, Jundiaí) e atende 11 capitais brasileiras.',
    },
    {
      question: 'Quando devo escolher a WST em vez da STIB?',
      answer:
        'Escolha a WST se você precisa de produção completa de eventos (cenografia, iluminação, vídeo, tradução simultânea) além dos fones. Escolha a STIB se o seu foco principal é a qualidade dos fones, o custo-benefício e a logística multi-cidade.',
    },
  ],
  metaTitle: 'STIB vs World Sound Translation: Qual Melhor para Palestra Silenciosa?',
  metaDescription:
    'Comparativo completo: STIB vs World Sound Translation. Veja qual empresa oferece melhor custo-benefício, cobertura e qualidade de fones para palestra silenciosa no Brasil.',
  heroImageSeq: 'comparativowsthero',
};

// ─── Competitor 2: Green Sound ────────────────────────────────────────────────

export const GREEN_SOUND_DATA: CompetitorData = {
  slug: 'stib-vs-green-sound',
  name: 'Green Sound',
  fullName: 'Green Sound',
  website: 'greensound.com.br',
  location: 'São Paulo, SP',
  phone: '(11) 3804-5686',
  yearsInMarket: 10,
  eventsCount: 'Não divulgado',
  equipmentCount: 'Não divulgado',
  satisfactionRate: 0,
  geoCoverage: 'São Paulo (foco)',
  geoCoverageScore: 4,
  specializationScore: 5,
  equipmentScore: 6,
  experienceScore: 6,
  serviceScore: 5,
  priceScore: 4,
  techScore: 7,
  supportScore: 5,
  overallScore: 5.1,
  tagline: 'Soluções em áudio com tecnologia alemã',
  description:
    'A Green Sound é uma empresa com mais de 10 anos de atuação no mercado de áudio para eventos. Seu principal diferencial é a tecnologia alemã nos equipamentos, prometendo fones de ouvido, transmissores e cabines de última geração. Oferece serviços de tradução simultânea, palestra silenciosa, visita guiada, áudio descrição e eventos corporativos em geral.',
  keyDifferentiator: 'Equipamentos com tecnologia alemã e mais de 10 anos de experiência no mercado de áudio para eventos.',
  strengths: [
    'Tecnologia alemã nos equipamentos',
    'Mais de 10 anos de experiência',
    'Atendimento personalizado',
    'Compromisso com o sucesso do evento',
  ],
  weaknesses: [
    'Não divulga quantidade de equipamentos ou número de eventos',
    'Cobertura geográfica limitada (foco em São Paulo)',
    'Não informa taxa de satisfação dos clientes',
    'Site não detalha capacidade máxima de participantes',
  ],
  idealFor: [
    'Eventos que valorizam tecnologia de equipamento premium',
    'Clientes que buscam atendimento personalizado',
    'Eventos de porte médio em São Paulo',
  ],
  comparisonCategories: [
    {
      label: 'Especialização em Fones',
      stibValue: '100% focada — aluguel de fones é o core',
      competitorValue: 'Áudio geral com fones como um dos serviços',
      winner: 'stib',
    },
    {
      label: 'Tecnologia dos Equipamentos',
      stibValue: 'Fones RF de última geração com alcance 500m',
      competitorValue: 'Tecnologia alemã (marca não divulgada)',
      winner: 'competitor',
    },
    {
      label: 'Cobertura Geográfica',
      stibValue: '9 cidades SP + 11 capitais brasileiras',
      competitorValue: 'Foco em São Paulo',
      winner: 'stib',
    },
    {
      label: 'Equipamentos Disponíveis',
      stibValue: '5.000+ fones sem fio',
      competitorValue: 'Quantidade não divulgada',
      winner: 'stib',
    },
    {
      label: 'Transparência de Dados',
      stibValue: '500+ eventos, 780+ clientes, 98% satisfação',
      competitorValue: 'Não divulga volume de eventos ou satisfação',
      winner: 'stib',
    },
    {
      label: 'Experiência no Mercado',
      stibValue: '8 anos — foco exclusivo em fones',
      competitorValue: '10+ anos — áudio geral',
      winner: 'tie',
    },
    {
      label: 'Preço Competitivo',
      stibValue: 'Custo otimizado por especialização',
      competitorValue: 'Tecnologia premium pode elevar custos',
      winner: 'stib',
    },
    {
      label: 'Atendimento ao Cliente',
      stibValue: 'Equipe técnica presencial em 9 cidades',
      competitorValue: 'Atendimento personalizado (foco SP)',
      winner: 'stib',
    },
  ],
  faqs: [
    {
      question: 'A tecnologia alemã da Green Sound é superior aos fones da STIB?',
      answer:
        'A Green Sound menciona tecnologia alemã, mas não especifica a marca ou modelo dos equipamentos. A STIB utiliza fones sem fio RF de última geração com alcance de até 500 metros e bateria para 12+ horas. A qualidade final depende mais da configuração técnica e da equipe de suporte do que da origem do equipamento.',
    },
    {
      question: 'A STIB tem mais equipamentos que a Green Sound?',
      answer:
        'A STIB disponibiliza 5.000+ fones sem fio em estoque. A Green Sound não divulga a quantidade de equipamentos disponíveis. Para eventos grandes (1.000+ pessoas), a STIB tem capacidade comprovada.',
    },
    {
      question: 'Qual empresa tem melhor custo-benefício?',
      answer:
        'A STIB tende a ter melhor custo-benefício para locação pura de fones, pois é 100% especializada nesse serviço. A Green Sound pode ter preços premium devido à tecnologia alemã, mas sem dados transparentes de volume, a comparação é parcial.',
    },
    {
      question: 'A Green Sound atende fora de São Paulo?',
      answer:
        'O site da Green Sound indica atendimento em São Paulo. A STIB tem cobertura estratégica em 9 cidades do interior paulista e atende 11 capitais brasileiras, incluindo Rio de Janeiro, Belo Horizonte, Curitiba e Brasília.',
    },
    {
      question: 'Quando a Green Sound é a melhor escolha?',
      answer:
        'A Green Sound pode ser indicada se você prioriza especificamente tecnologia alemã em equipamentos e eventos de porte médio em São Paulo. Para eventos grandes, multi-cidade ou quando a transparência de dados é prioridade, a STIB oferece vantagens claras.',
    },
  ],
  metaTitle: 'STIB vs Green Sound: Qual Melhor para Aluguel de Fones sem Fio?',
  metaDescription:
    'Comparativo detalhado STIB vs Green Sound. Veja quem tem melhor tecnologia, preço, cobertura e equipamentos para palestra silenciosa e eventos corporativos.',
  heroImageSeq: 'comparativogreenhero',
};

// ─── Competitor 3: Palestra Silenciosa SBJ ───────────────────────────────────

export const SBJ_DATA: CompetitorData = {
  slug: 'stib-vs-palestra-silenciosa-sbj',
  name: 'Palestra Silenciosa SBJ',
  fullName: 'Palestra Silenciosa SBJ',
  website: 'palestrasilenciosa.com',
  location: 'São Paulo, SP',
  phone: '(11) 3773-7711',
  yearsInMarket: 30,
  eventsCount: 'Não divulgado',
  equipmentCount: 'Não divulgado',
  satisfactionRate: 0,
  geoCoverage: 'Brasil, USA, Europa',
  geoCoverageScore: 5,
  specializationScore: 6,
  equipmentScore: 4,
  experienceScore: 9,
  serviceScore: 4,
  priceScore: 3,
  techScore: 4,
  supportScore: 4,
  overallScore: 4.8,
  tagline: '30 anos de palestra silenciosa no mercado',
  description:
    'A Palestra Silenciosa SBJ é uma das empresas mais antigas do mercado, com 30 anos de atuação. Oferece palestras silenciosas com tecnologia avançada, incluindo fones WiFi de alta qualidade, votação interativa, relatórios de resultados e estatísticas de participantes. Atende Brasil, Estados Unidos e Europa.',
  keyDifferentiator: '30 anos de experiência no mercado e atendimento internacional (Brasil, USA e Europa).',
  strengths: [
    '30 anos de experiência no mercado',
    'Atendimento internacional (Brasil, USA, Europa)',
    'Portfólio visual extenso com fotos de eventos',
    'Soluções de interatividade (votação, quiz)',
  ],
  weaknesses: [
    'Site desatualizado (visual antigo, pouca informação estruturada)',
    'Não divulga quantidade de equipamentos disponíveis',
    'Não informa taxa de satisfação ou número de clientes',
    'Tecnologia WiFi pode sofrer interferências em ambientes com muitos dispositivos',
    'Não detalha preços ou capacidade máxima',
  ],
  idealFor: [
    'Clientes que valorizam tradição e longevidade no mercado',
    'Eventos internacionais que precisam de atendimento fora do Brasil',
    'Eventos com foco em interatividade e votação ao vivo',
  ],
  comparisonCategories: [
    {
      label: 'Experiência no Mercado',
      stibValue: '8 anos — 500+ eventos focados',
      competitorValue: '30 anos — histórico longo',
      winner: 'competitor',
    },
    {
      label: 'Cobertura Geográfica',
      stibValue: '9 cidades SP + 11 capitais brasileiras',
      competitorValue: 'Brasil, USA, Europa',
      winner: 'competitor',
    },
    {
      label: 'Tecnologia dos Fones',
      stibValue: 'Fones RF sem interferência (alcance 500m)',
      competitorValue: 'Fones WiFi (suscetível a interferências)',
      winner: 'stib',
    },
    {
      label: 'Transparência de Dados',
      stibValue: '5.000+ fones, 500+ eventos, 780+ clientes',
      competitorValue: 'Nenhum dado operacional divulgado',
      winner: 'stib',
    },
    {
      label: 'Custo-Benefício',
      stibValue: 'Preços competitivos por especialização',
      competitorValue: 'Preço premium por experiência histórica',
      winner: 'stib',
    },
    {
      label: 'Capacidade de Escala',
      stibValue: '5.000+ participantes simultâneos',
      competitorValue: 'Capacidade não divulgada',
      winner: 'stib',
    },
    {
      label: 'Interatividade',
      stibValue: 'Fones com múltiplos canais e votação',
      competitorValue: 'Quiz, votação, estatísticas avançadas',
      winner: 'competitor',
    },
    {
      label: 'Presença Digital',
      stibValue: 'Site moderno, SEO, orçamento online',
      competitorValue: 'Site desatualizado, pouca info online',
      winner: 'stib',
    },
  ],
  faqs: [
    {
      question: 'A Palestra Silenciosa SBJ tem mais experiência que a STIB?',
      answer:
        'Sim em termos de anos de mercado — 30 anos vs 8 anos. Porém, a STIB realizou 500+ eventos de palestra silenciosa documentados, enquanto a SBJ não divulga o volume de eventos. A experiência prática depende mais do volume de eventos do que apenas do tempo de mercado.',
    },
    {
      question: 'Os fones WiFi da SBJ são melhores que os fones RF da STIB?',
      answer:
        'Depende do ambiente. Fones WiFi oferecem mais funcionalidades (votação, quiz), mas são suscetíveis a interferências em locais com muitos dispositivos conectados. Fones RF (rádio frequência) da STIB são mais estáveis em grandes eventos com milhares de participantes, com alcance de até 500 metros sem depender de rede WiFi do local.',
    },
    {
      question: 'A STIB atende eventos fora do Brasil?',
      answer:
        'A STIB atende 11 capitais brasileiras com cobertura completa. A Palestra Silenciosa SBJ menciona atendimento em Brasil, USA e Europa. Se seu evento é internacional, a SBJ pode ser mais indicada. Para eventos no Brasil, a STIB oferece cobertura mais estruturada.',
    },
    {
      question: 'Qual empresa tem melhor preço?',
      answer:
        'A STIB tende a ter preços mais competitivos para eventos no Brasil devido à especialização e estrutura enxuta. A SBJ, com 30 anos de mercado e atendimento internacional, pode ter preços premium. Para um orçamento justo, recomendamos solicitar cotação de ambas.',
    },
    {
      question: 'A STIB oferece interatividade como quiz e votação?',
      answer:
        'Sim! A STIB oferece fones com múltiplos canais de áudio, permitindo votação interativa e múltiplas trilhas simultâneas. A SBJ tem recursos similares. A diferença principal está na tecnologia de transmissão: RF (STIB) é mais estável em grandes eventos; WiFi (SBJ) oferece mais integração digital.',
    },
  ],
  metaTitle: 'STIB vs Palestra Silenciosa SBJ: Quem Tem Melhor Tecnologia e Preço?',
  metaDescription:
    'Comparativo honesto: STIB vs Palestra Silenciosa SBJ. Veja diferenças de tecnologia (RF vs WiFi), preço, cobertura e qualidade para palestra silenciosa no Brasil.',
  heroImageSeq: 'comparativosbjhero',
};

// ─── Competitor 4: AB Traduções ──────────────────────────────────────────────

export const ABTRADUCOES_DATA: CompetitorData = {
  slug: 'stib-vs-ab-traducoes',
  name: 'AB Traduções',
  fullName: 'AB Traduções',
  website: 'abtraducoes.com.br',
  location: 'Curitiba, PR',
  phone: '(41) 3618-9376',
  yearsInMarket: 15,
  eventsCount: 'Não divulgado',
  equipmentCount: 'Não divulgado',
  satisfactionRate: 0,
  geoCoverage: '15 cidades no Brasil',
  geoCoverageScore: 6,
  specializationScore: 3,
  equipmentScore: 4,
  experienceScore: 7,
  serviceScore: 5,
  priceScore: 4,
  techScore: 4,
  supportScore: 5,
  overallScore: 4.8,
  tagline: 'Agência de tradução com palestra silenciosa como serviço',
  description:
    'A AB Traduções é uma empresa de tradução simultânea e escrita sediada em Curitiba, com atendimento em 15 cidades do Brasil. Oferece serviços completos de tradução: simultânea, consecutiva, remota (RSI), escrita, transcrição, interpretação de libras e palestra silenciosa. A palestra silenciosa é um dos muitos serviços oferecidos, não o foco principal.',
  keyDifferentiator: 'Agência de tradução completa com cobertura em 15 cidades brasileiras e sede em Curitiba.',
  strengths: [
    'Cobertura em 15 cidades brasileiras',
    'Serviços de tradução completos (simultânea, consecutiva, remota)',
    'Interpretação em libras',
    'Processo de qualidade estruturado (9 etapas)',
  ],
  weaknesses: [
    'Palestra silenciosa é serviço secundário (não é o core business)',
    'Sede em Curitiba — logística mais complexa para SP',
    'Não divulga quantidade de fones ou capacidade',
    'Não informa taxa de satisfação dos clientes',
    'Site não detalha tecnologia dos equipamentos',
  ],
  idealFor: [
    'Eventos que precisam de tradução simultânea + palestra silenciosa juntas',
    'Empresas sediadas no Sul/Sudeste fora de SP',
    'Eventos que exigem interpretação em libras',
  ],
  comparisonCategories: [
    {
      label: 'Foco no Serviço',
      stibValue: '100% especializada em fones para eventos',
      competitorValue: 'Tradução é o core; fones são secundário',
      winner: 'stib',
    },
    {
      label: 'Cobertura em São Paulo',
      stibValue: '9 cidades em SP com equipe local',
      competitorValue: '15 cidades no Brasil (Curitiba como base)',
      winner: 'stib',
    },
    {
      label: 'Equipamentos',
      stibValue: '5.000+ fones sem fio documentados',
      competitorValue: 'Quantidade não divulgada',
      winner: 'stib',
    },
    {
      label: 'Tradução Simultânea',
      stibValue: 'Tradução sem cabine via fones',
      competitorValue: 'Tradução completa (cabine + fones + RSI)',
      winner: 'competitor',
    },
    {
      label: 'Preço para Fones',
      stibValue: 'Preço otimizado por especialização',
      competitorValue: 'Preço de agência de tradução (premium)',
      winner: 'stib',
    },
    {
      label: 'Interpretação em Libras',
      stibValue: 'Não oferece (foco em áudio multicanal)',
      competitorValue: 'Oferece interpretação em libras',
      winner: 'competitor',
    },
    {
      label: 'Experiência',
      stibValue: '8 anos — 500+ eventos de fones',
      competitorValue: '15+ anos — tradução em geral',
      winner: 'competitor',
    },
    {
      label: 'Transparência',
      stibValue: 'Dados completos: eventos, clientes, satisfação',
      competitorValue: 'Não divulga métricas operacionais',
      winner: 'stib',
    },
  ],
  faqs: [
    {
      question: 'A AB Traduções é especialista em palestra silenciosa?',
      answer:
        'Não. A AB Traduções é uma agência de tradução completa. A palestra silenciosa é um dos serviços oferecidos, mas o core business é tradução simultânea, consecutiva, escrita e interpretação em libras. Se você precisa de palestra silenciosa como serviço principal, uma empresa especializada como a STIB tende a oferecer melhor expertise.',
    },
    {
      question: 'A STIB oferece tradução simultânea?',
      answer:
        'Sim! A STIB oferece tradução simultânea sem cabine usando fones sem fio. A vantagem é que não é necessário construir cabines de tradução no local, reduzindo custos e logística. A AB Traduções oferece tradução simultânea tradicional (com cabine) e remota (RSI).',
    },
    {
      question: 'Qual empresa tem melhor preço para eventos em São Paulo?',
      answer:
        'Para eventos em São Paulo, a STIB tende a ter vantagem logística e de custo, pois tem equipe local em 9 cidades do estado. A AB Traduções é sediada em Curitiba, o que pode gerar custos adicionais de deslocamento para eventos na capital paulista.',
    },
    {
      question: 'A AB Traduções atende mais cidades que a STIB?',
      answer:
        'A AB Traduções menciona 15 cidades no Brasil. A STIB cobre 9 cidades em São Paulo + 11 capitais brasileiras, totalizando 20+ cidades com cobertura própria ou parceiros. A STIB tem presença mais densa no estado de São Paulo.',
    },
    {
      question: 'Quando escolher a AB Traduções em vez da STIB?',
      answer:
        'Escolha a AB Traduções se seu evento precisa de tradução simultânea completa (cabine + intérpretes + fones) e está localizado no Sul do Brasil. Escolha a STIB se o foco é palestra silenciosa pura, custo-benefício em fones e eventos em São Paulo ou outras capitais.',
    },
  ],
  metaTitle: 'STIB vs AB Traduções: Quem é Melhor para Palestra Silenciosa?',
  metaDescription:
    'Comparativo STIB vs AB Traduções. Veja diferenças de especialização, preço, cobertura e tecnologia para palestra silenciosa e eventos corporativos no Brasil.',
  heroImageSeq: 'comparativoabhero',
};

// ─── All competitors array ─────────────────────────────────────────────────────

export const ALL_COMPETITORS = [WST_DATA, GREEN_SOUND_DATA, SBJ_DATA, ABTRADUCOES_DATA];

export function getCompetitorBySlug(slug: string): CompetitorData | undefined {
  return ALL_COMPETITORS.find((c) => c.slug === slug);
}